/* global API */

import formatPrice from '../helpers/formatPrice';
import dataLayerHelper from '../helpers/dataLayer';

const $cart = $('.js-cart');
const $total = $('.js-cart-total');
const $orderBtn = $('.js-cart-order-btn');
const $personsError = $('.js-cart-persons-error');

const ROW_SELECTOR = '.js-cart-item';
const ROW_TOTAL_SELECTOR = '.js-cart-item-total';
const ROW_QUANTITY_SELECTOR = '.js-cart-item-quantity';
const ROW_DELETE_SELECTOR = '.js-cart-item-delete';

const cart = {
  initialize() {
    if ($cart.length) {
      this.minPersons = parseInt($cart.data('min-persons'), 10);
      this.getItems = () => { this.items = $('.js-cart-item').toArray(); };
      this.getItems();

      this.createEvents();
    }
  },

  /**
   * Hook up all events for the cart
   * @return {void}
   */
  createEvents() {
    $cart.on('change', ROW_QUANTITY_SELECTOR, (e) => {
      const $row = this.getCartRow($(e.currentTarget));

      this.recalculateRow($row);
      this.recalculateTotals();
    });

    $cart.on('recalculate', () => {
      this.recalculateTotals();
    });

    $cart.on('click', ROW_DELETE_SELECTOR, (e) => {
      const $row = this.getCartRow($(e.currentTarget));

      this.hideRow($row)
        .then(this.removeRow.bind(this))
        .then(this.recalculateTotals.bind(this));
    });
  },

  /**
   * Retrieve the parent cart row for given element
   * @param {Object} $el jQuery element
   * @return {Object} Cart row
   */
  getCartRow($el) {
    return $el.parents(ROW_SELECTOR);
  },

  /**
   * Get price per piece for the row
   * @param {Object} $row jQuery element of the product row
   * @param {String|Number} fallback Fallback for when the value is not a number
   * @return {Number}
   */
  getPriceForRow($row, fallback = 0) {
    const val = parseFloat($row.data('price'));
    return (Number.isNaN(val)) ? fallback : val;
  },

  /**
   * Get quantity of the product row
   * @param {Object} $row jQuery element of the product row
   * @param {String|Number} fallback Fallback for when the value is not a number
   * @return {Number}
   */
  getQuantityForRow($row, fallback = 1) {
    const val = parseInt($row.find(ROW_QUANTITY_SELECTOR).val(), 10);
    return (Number.isNaN(val)) ? fallback : val;
  },

  /**
   * Output the total price for the row
   * @param {Object} $row jQuery element of the product row
   * @param {Number} total Total price
   * @return {void}
   */
  outputRowTotal($row, total) {
    $row
      .data('total', total)
      .find(ROW_TOTAL_SELECTOR)
      .html(formatPrice(total));
  },

  /**
   * Recalculate the total of the product row
   * @param {Object} $row jQuery element of the product row
   * @return {void}
   */
  recalculateRow($row) {
    const price = this.getPriceForRow($row);
    const quantity = this.getQuantityForRow($row);
    const total = price * quantity;
    const prevQuantity = parseInt($row.data('quantity'), 10);

    $row.data('quantity', quantity);

    if (typeof $row.data('product') !== 'undefined') {
      const diffQuantity = quantity - prevQuantity;

      if (diffQuantity > 0) {
        this.gtmAdd($row.data('product'), diffQuantity);
      } else if (diffQuantity < 0) {
        this.gtmRemove($row.data('product'), diffQuantity);
      }

      this.cartChange($row.data('id'), quantity);
    }

    this.outputRowTotal($row, total);
  },

  /**
   * Calculate the cart total and output
   * @return {void}
   */
  recalculateTotals() {
    this.getItems();

    const total = this.items.reduce((acc, item) => (
      acc + parseFloat($(item).data('total'))
    ), 0);

    const totalPackagePersons = this.items.reduce((acc, item) => {
      if (!$(item).data('is-package')) {
        return acc;
      }

      return acc + this.getQuantityForRow($(item));
    }, 0);

    this.checkMinPersons(totalPackagePersons);

    $total.html(formatPrice(total));

    $(document).trigger('cart:change', total);
  },

  /**
   * Check if the number of persons is a valid number of persons
   * @param {Number} persons
   * @return {void}
   */
  checkMinPersons(persons) {
    let isValid = true;

    if (persons < this.minPersons) {
      isValid = false;
    }

    $personsError.toggle(!isValid);
    $orderBtn.prop('disabled', !isValid);
  },

  /**
   * Hide the row visually
   * @param {Object} $row jQuery element of the row
   * @return {Promise}
   */
  hideRow($row) {
    return new Promise((resolve) => {
      // Fade the product out and resolve the promise on completion
      $row.animate({
        transform: 'translate3d(-15px, 0, 0)',
        opacity: 0,
      }, { duration: 400, done: () => resolve($row) });
    });
  },

  /**
   * Remove the row from the DOM
   * @param {Object} $row jQuery element of the row
   * @return {Promise}
   */
  removeRow($row) {
    return new Promise((resolve) => {
      // Create a replacement div that will keep the space
      const $replacement = $('<div />');
      $replacement.css('height', $row.outerHeight(true));

      // Add the replacement div
      $row.before($replacement);

      // Remove the product row
      $row.remove();

      const quantity = parseInt($row.find(ROW_QUANTITY_SELECTOR).val(), 10);

      if (typeof $row.data('product') !== 'undefined') {
        this.gtmRemove($row.data('product'), quantity);
      }

      this.cartChange($row.data('id'), 0);

      // Animate the replacement div to hide it slowly
      $replacement.animate({
        height: 0,
      }, {
        duration: 400,
        done: () => {
          // Remove the last traces of the product removal
          $replacement.remove();
          resolve();
        },
      });
    });
  },

  /**
   * Add products to GTM
   * @param {Object} data GTM object
   * @param {Number} quantityIncrement Amount to increment the product
   * @return {void}
   */
  gtmAdd(data, quantityIncrement) {
    data.quantity = quantityIncrement;
    dataLayerHelper.addProduct(data);
  },

  /**
   * Remove products from GTM
   * @param {Object} data GTM object
   * @param {Number} quantityIncrement Amount to decrement the product
   * @return {void}
   */
  gtmRemove(data, quantityIncrement) {
    data.quantity = Math.abs(quantityIncrement);
    dataLayerHelper.removeProduct(data);
  },

  /**
   * Call API with cart changes
   * @param {Number} id
   * @param {Number} quantity
   * @return {void}
   */
  cartChange(id, quantity) {
    $.ajax({
      url: `${API.cartChange}/${id}`,
      type: 'POST',
      data: {
        quantity,
        _method: 'put',
      },
      success: () => {
        $(document).trigger('cart:update');
      },
    });
  },
};

export default cart;
